/* Single draggable Kanban card on the Pipeline board.
 *
 * Extracted from `pages/app/Pipeline.jsx` (Jun 2026) when that file
 * crossed 500 lines. The card is dumb — every interactive callback
 * (drag, open, delete, call, sign) is passed in from the parent so
 * the same component would render identically in a future "Pipeline
 * preview" surface (e.g. inside the Buyer Detail page) without
 * duplicating state.
 *
 * Visual contract (do not change without screenshot diffing the
 * Pipeline board): card has a colored top border tied to `stage`,
 * a header row with address + open + delete icon buttons, an
 * optional sub-row of badges (AI Score / Pro-lock / Rehab / Best
 * Exit / Last call), an ARV-vs-source footer split, and a 3-button
 * primary-action row (Rec call · Tel link · Send for Signature).
 */
import { MapPin, ExternalLink, Trash2, Flame, Clock, PhoneCall, FileSignature, Lock,
         Phone, PhoneOff, Voicemail, AlertCircle, MessageCircle } from "lucide-react";
import { fmtMoney, STAGE_COLOR } from "@/lib/api";
import CallButton from "@/components/CallButton";
import { RehabBadge } from "@/components/RehabScopeCard";
import { openUpgradeModal } from "@/components/UpgradeToProModal";

/* Outcome → icon+tone pill for the "Last:" badge. Mirrors QuickCallModal. */
const OUTCOME_META = {
  contact:    { Icon: Phone,          label: "Contacted",       tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  voicemail:  { Icon: Voicemail,      label: "Voicemail",       tone: "bg-amber-50 text-amber-700 border-amber-200" },
  no_answer:  { Icon: PhoneOff,       label: "No answer",       tone: "bg-slate-50 text-slate-600 border-slate-200" },
  wrong:      { Icon: AlertCircle,    label: "Wrong number",    tone: "bg-rose-50 text-rose-700 border-rose-200" },
  callback:   { Icon: PhoneCall,      label: "Callback",        tone: "bg-sky-50 text-sky-700 border-sky-200" },
  other:      { Icon: MessageCircle,  label: "Logged",          tone: "bg-stone-50 text-stone-600 border-stone-200" },
};

function relativeTime(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.round(diff / 60)}m`;
  if (diff < 86400) return `${Math.round(diff / 3600)}h`;
  return `${Math.round(diff / 86400)}d`;
}

export default function KanbanCard({
  lead: l,
  stage: s,
  isDragging,
  primaryAction,
  isProOrAbove,
  onDragStart,
  onDragEnd,
  onOpen,
  onDelete,
  onCall,
  onSign,
}) {
  return (
    <div
      className={"kanban-card" + (isDragging ? " dragging" : "")}
      style={{ borderTopColor: STAGE_COLOR[s] }}
      draggable
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      data-testid={`kanban-card-${l.id}`}
    >
      {/* Header: address + open + delete */}
      <div className="font-semibold text-sm flex items-start gap-1.5">
        <MapPin size={12} className="mt-1 shrink-0 text-[var(--ink-3)]" />
        <span className="leading-tight flex-1">{l.property_address}</span>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          className="shrink-0 w-5 h-5 rounded hover:bg-slate-100 text-[var(--ink-3)] hover:text-emerald-600 flex items-center justify-center transition"
          title="Open lead detail (recordings, AI Coach, contracts)"
          data-testid={`kanban-open-${l.id}`}
          draggable={false}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <ExternalLink size={11} />
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
          className="shrink-0 w-5 h-5 rounded hover:bg-rose-50 text-[var(--ink-3)] hover:text-rose-600 flex items-center justify-center transition"
          title="Delete lead (or move to Dead instead)"
          data-testid={`kanban-delete-${l.id}`}
          draggable={false}
          onMouseDown={(e) => e.stopPropagation()}
        >
          <Trash2 size={11} />
        </button>
      </div>

      <div className="text-xs text-[var(--ink-2)] mt-1 ml-[18px]">
        {[l.city, l.state].filter(Boolean).join(", ")}
      </div>

      {/* AI Score chip (Pro+ scored leads) */}
      {typeof l.ai_score === "number" && (
        <div
          className={`mt-2 ml-[18px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold border ${
            l.ai_score_label === "hot"
              ? "bg-rose-50 text-rose-700 border-rose-200"
              : l.ai_score_label === "warm"
              ? "bg-amber-50 text-amber-700 border-amber-200"
              : "bg-slate-50 text-slate-600 border-slate-200"
          }`}
          title={l.ai_score_reasoning || "AI Score"}
          data-testid={`kanban-ai-score-${l.id}`}
        >
          <span>{l.ai_score_label === "hot" ? "🔥" : l.ai_score_label === "warm" ? "✨" : "❄️"}</span>
          <span>AI {l.ai_score}/100</span>
        </div>
      )}

      {/* Pro-lock badge: Free/Basic users see the upsell where their score would be */}
      {typeof l.ai_score !== "number" && !isProOrAbove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            openUpgradeModal({ feature: "lead-scoring" });
          }}
          onMouseDown={(e) => e.stopPropagation()}
          draggable={false}
          className="mt-2 ml-[18px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold border bg-gradient-to-r from-emerald-50 to-amber-50 text-emerald-700 border-emerald-200 hover:from-emerald-100 hover:to-amber-100 transition"
          title="Upgrade to Pro — Claude scores every lead 0-100 with motivation + urgency + equity + reachability"
          data-testid={`kanban-ai-score-lock-${l.id}`}
        >
          <Lock size={9} className="text-emerald-700" />
          <span>Pro · AI Lead Score</span>
        </button>
      )}

      {l.rehab_estimate?.range_mid && (
        <div className="mt-2 ml-[18px]">
          <RehabBadge estimate={l.rehab_estimate} compact />
        </div>
      )}

      {l.best_exit && (
        <div
          className="mt-2 ml-[18px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200"
          data-testid={`kanban-best-exit-${l.id}`}
          title={`Best exit: ${l.best_exit.label}`}
        >
          {l.best_exit.label} · {fmtMoney(l.best_exit.profit)}
        </div>
      )}

      {l.last_call && (() => {
        const meta = OUTCOME_META[l.last_call.outcome] || OUTCOME_META.other;
        const { Icon, label, tone } = meta;
        return (
          <div
            className={`mt-2 ml-[18px] inline-flex items-center gap-1 px-1.5 py-0.5 rounded-md text-[10px] font-semibold border ${tone}`}
            data-testid={`kanban-last-call-${l.id}`}
            title={`Last call: ${label} · ${new Date(l.last_call.created_at).toLocaleString()}`}
          >
            <Icon size={10} />
            <span>{label}</span>
            <Clock size={9} className="opacity-60" />
            <span className="opacity-75">{relativeTime(l.last_call.created_at)}</span>
          </div>
        );
      })()}

      {/* ARV vs Source footer split */}
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[var(--line)]">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)]">ARV</div>
          <div className="text-sm font-mono font-semibold tabular-nums">{fmtMoney(l.estimated_arv)}</div>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-widest text-[var(--ink-3)]">Source</div>
          <div className="text-xs font-medium">{l.lead_source}</div>
        </div>
      </div>

      {/* 3-button action row: Rec call · Tel · Send for Signature */}
      <div className="grid grid-cols-[auto_auto_1fr] gap-2 mt-2">
        <span
          onMouseDown={(e) => e.stopPropagation()}
          onClick={(e) => e.stopPropagation()}
          className={primaryAction === "call" ? "ring-2 ring-emerald-400/60 rounded-md" : "opacity-90"}
          data-primary={primaryAction === "call" ? "true" : "false"}
        >
          <CallButton phone={l.seller_phone} variant="full" label="Rec call" />
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            if (!l.seller_phone) return;
            try {
              const w = window.open(`tel:${l.seller_phone}`, "_blank");
              if (!w) window.location.href = `tel:${l.seller_phone}`;
            } catch { /* fall through */ }
            onCall();
          }}
          disabled={!l.seller_phone}
          className={
            "inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] font-semibold border transition " +
            (l.seller_phone
              ? "border-slate-300 text-slate-700 hover:bg-slate-50"
              : "border-[var(--line)] text-[var(--ink-3)] cursor-not-allowed opacity-60")
          }
          title={l.seller_phone ? "Tel-link call (no recording)" : "No seller phone on file"}
          data-testid={`kanban-call-${l.id}`}
        >
          <PhoneCall size={12} />
          Tel
        </button>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onSign(); }}
          className={
            "inline-flex items-center justify-center gap-1.5 px-2 py-1.5 rounded-md text-[11px] font-semibold border transition " +
            (primaryAction === "signature"
              ? "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700 shadow-sm ring-2 ring-emerald-400/40"
              : "border-emerald-200 text-emerald-700 hover:bg-emerald-50")
          }
          data-primary={primaryAction === "signature" ? "true" : "false"}
          data-testid={`kanban-send-signature-${l.id}`}
        >
          <FileSignature size={12} />
          Send for Signature
        </button>
      </div>
    </div>
  );
}

/* Re-export needsFollowUp-relevant helper used by the parent column header.
 * Just a convenience so Pipeline.jsx stays slim. */
export { Flame };
